import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const RANKING_BUYS_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/ranking_model_buys.json";

const SCREENER_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/stock_screener.json";

const RETURNS_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/data/daily_returns.csv";

const STOCK_DETAIL_PATH = "/stocks2";

function parseNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(String(value).replace(/[$,%]/g, "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `$${n.toFixed(2)}`;
}

function formatDollars(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function formatCompactDollars(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";

  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
}

function parseCsvLine(line) {
  const out = [];
  let cur = "";
  let inQuotes = false;

  for (let i = 0; i < String(line || "").length; i++) {
    const ch = String(line || "")[i];
    const next = String(line || "")[i + 1];

    if (ch === '"') {
      if (inQuotes && next === '"') {
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      out.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }

  out.push(cur);
  return out;
}

function formatShares(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(4);
}

function formatScore(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(1);
}

function formatPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  const sign = n > 0 ? "+" : "";
  return `${sign}${(n * 100).toFixed(2)}%`;
}

function formatAllocationPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${(n * 100).toFixed(2)}%`;
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;

  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;

  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function returnColor(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return neutralPill;
  if (n > 0) return positivePill;
  if (n < 0) return negativePill;
  return neutralPill;
}

function scoreColor(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return neutralScorePill;
  if (n >= 80) return excellentScorePill;
  if (n >= 65) return goodScorePill;
  if (n >= 50) return okayScorePill;
  return weakScorePill;
}

export default function ModelPage() {
  const [logData, setLogData] = useState(null);
  const [screenerData, setScreenerData] = useState([]);
  const [dailyReturns, setDailyReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [selectedDate, setSelectedDate] = useState("ALL");
  const [showOnlyLatest, setShowOnlyLatest] = useState(false);

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        setError("");

        const [buysRes, screenerRes, returnsRes] = await Promise.all([
          fetch(RANKING_BUYS_URL, { cache: "no-store" }),
          fetch(SCREENER_URL, { cache: "no-store" }),
          fetch(RETURNS_URL, { cache: "no-store" }),
        ]);

        const buysText = await buysRes.text();

        if (!buysRes.ok) {
          throw new Error(`Ranking buys fetch failed: ${buysRes.status}`);
        }

        if (buysText.trim().startsWith("<")) {
          throw new Error(
            "Expected ranking_model_buys.json but received HTML. Check the GitHub path."
          );
        }

        const buysJson = JSON.parse(buysText);
        setLogData(buysJson);

        if (screenerRes.ok) {
          const screenerText = await screenerRes.text();

          if (!screenerText.trim().startsWith("<")) {
            const screenerJson = JSON.parse(screenerText);
            setScreenerData(Array.isArray(screenerJson.data) ? screenerJson.data : []);
          }
        }

        if (returnsRes.ok) {
          const returnsText = await returnsRes.text();

          if (!returnsText.trim().startsWith("<")) {
            const lines = returnsText.trim().split(/\r?\n/);

            if (lines.length >= 2) {
              const headers = parseCsvLine(lines[0]).map((h) =>
                String(h || "").trim().toUpperCase()
              );

              const parsed = [];

              for (let i = 1; i < lines.length; i++) {
                const cols = parseCsvLine(lines[i]);
                const date = String(cols[0] || "").trim();

                if (!date) continue;

                const returns = {};

                for (let c = 1; c < headers.length; c++) {
                  const ticker = headers[c];
                  if (!ticker) continue;

                  const rawReturn = parseNumber(cols[c]);

                  if (Number.isFinite(rawReturn)) {
                    returns[ticker] = rawReturn / 100;
                  }
                }

                parsed.push({
                  date,
                  returns,
                });
              }

              parsed.sort((a, b) => String(a.date).localeCompare(String(b.date)));
              setDailyReturns(parsed);
            }
          }
        }
      } catch (err) {
        console.error(err);
        setError(err.message || "Failed to load ranking model buys.");
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, []);

  const currentPriceByTicker = useMemo(() => {
    const map = {};

    for (const row of screenerData) {
      const ticker = String(row?.ticker || "").trim().toUpperCase();
      if (!ticker) continue;

      const price = parseNumber(row.price);
      if (Number.isFinite(price)) {
        map[ticker] = price;
      }
    }

    return map;
  }, [screenerData]);

  const buysByDate = useMemo(() => {
    const raw = Array.isArray(logData?.buys_by_date) ? logData.buys_by_date : [];

    return [...raw].sort((a, b) =>
      String(b?.date || "").localeCompare(String(a?.date || ""))
    );
  }, [logData]);

  const latestEntry = useMemo(() => {
    if (logData?.latest?.date) return logData.latest;
    return buysByDate[0] || null;
  }, [logData, buysByDate]);

  const dateOptions = useMemo(() => {
    return buysByDate.map((entry) => entry.date).filter(Boolean);
  }, [buysByDate]);

  const filteredEntries = useMemo(() => {
    let entries = showOnlyLatest && latestEntry ? [latestEntry] : buysByDate;

    if (selectedDate !== "ALL") {
      entries = entries.filter((entry) => entry.date === selectedDate);
    }

    const q = search.trim().toLowerCase();

    if (!q) return entries;

    return entries
      .map((entry) => {
        const picks = Array.isArray(entry.picks) ? entry.picks : [];

        return {
          ...entry,
          picks: picks.filter((pick) => {
            const ticker = String(pick?.ticker || "").toLowerCase();
            const name = String(pick?.name || "").toLowerCase();

            return ticker.includes(q) || name.includes(q);
          }),
        };
      })
      .filter((entry) => entry.picks.length > 0);
  }, [buysByDate, latestEntry, selectedDate, search, showOnlyLatest]);

  const allocationRows = useMemo(() => {
    const rows = [];

    for (const entry of buysByDate) {
      const date = entry?.date;
      const picks = Array.isArray(entry?.picks) ? entry.picks : [];

      for (const pick of picks) {
        const ticker = String(pick?.ticker || "").trim().toUpperCase();
        if (!ticker) continue;

        const buyPrice = parseNumber(pick.buy_price);
        const shares = parseNumber(pick.shares);
        const dollarsAllocated =
          parseNumber(pick.dollars_allocated) ||
          (Number.isFinite(buyPrice) && Number.isFinite(shares)
            ? buyPrice * shares
            : null);

        const currentPrice = currentPriceByTicker[ticker] ?? buyPrice;
        const currentValue =
          Number.isFinite(currentPrice) && Number.isFinite(shares)
            ? currentPrice * shares
            : dollarsAllocated;

        const returnPct =
          Number.isFinite(currentPrice) &&
          Number.isFinite(buyPrice) &&
          buyPrice > 0
            ? currentPrice / buyPrice - 1
            : null;

        rows.push({
          date,
          ticker,
          name: pick.name,
          action: pick.action || "BUY",
          rank: pick.rank,
          shares,
          buyPrice,
          dollarsAllocated,
          currentPrice,
          currentValue,
          returnPct,
          combinedScore: pick.combinedScore,
        });
      }
    }

    const totalCurrentValue = rows.reduce(
      (sum, row) => sum + (Number(row.currentValue) || 0),
      0
    );

    return rows
      .map((row) => ({
        ...row,
        allocationPct:
          totalCurrentValue > 0 && Number.isFinite(row.currentValue)
            ? row.currentValue / totalCurrentValue
            : null,
      }))
      .sort((a, b) => {
        const dateCompare = String(b.date || "").localeCompare(String(a.date || ""));
        if (dateCompare !== 0) return dateCompare;
        return Number(a.rank || 999) - Number(b.rank || 999);
      });
  }, [buysByDate, currentPriceByTicker]);

  const allocationSummary = useMemo(() => {
    const totalInvested = allocationRows.reduce(
      (sum, row) => sum + (Number(row.dollarsAllocated) || 0),
      0
    );

    const totalCurrentValue = allocationRows.reduce(
      (sum, row) => sum + (Number(row.currentValue) || 0),
      0
    );

    const totalReturnPct =
      totalInvested > 0 ? totalCurrentValue / totalInvested - 1 : null;

    return {
      totalInvested,
      totalCurrentValue,
      totalReturnPct,
      totalLots: allocationRows.length,
    };
  }, [allocationRows]);

  const uniqueHoldings = useMemo(() => {
    const grouped = {};

    for (const row of allocationRows) {
      const ticker = String(row.ticker || "").trim().toUpperCase();
      if (!ticker) continue;

      if (!grouped[ticker]) {
        grouped[ticker] = {
          ticker,
          name: row.name,
          totalShares: 0,
          totalInvested: 0,
          currentValue: 0,
          lots: 0,
          firstBuyDate: row.date,
          lastBuyDate: row.date,
        };
      }

      grouped[ticker].totalShares += Number(row.shares) || 0;
      grouped[ticker].totalInvested += Number(row.dollarsAllocated) || 0;
      grouped[ticker].currentValue += Number(row.currentValue) || 0;
      grouped[ticker].lots += 1;

      if (String(row.date || "") < String(grouped[ticker].firstBuyDate || row.date)) {
        grouped[ticker].firstBuyDate = row.date;
      }

      if (String(row.date || "") > String(grouped[ticker].lastBuyDate || row.date)) {
        grouped[ticker].lastBuyDate = row.date;
      }
    }

    const totalCurrentValue = Object.values(grouped).reduce(
      (sum, row) => sum + (Number(row.currentValue) || 0),
      0
    );

    return Object.values(grouped)
      .map((row) => {
        const avgBuyPrice =
          row.totalShares > 0 ? row.totalInvested / row.totalShares : null;

        const currentPrice =
          row.totalShares > 0 ? row.currentValue / row.totalShares : null;

        const returnPct =
          row.totalInvested > 0 ? row.currentValue / row.totalInvested - 1 : null;

        const allocationPct =
          totalCurrentValue > 0 ? row.currentValue / totalCurrentValue : null;

        return {
          ...row,
          avgBuyPrice,
          currentPrice,
          returnPct,
          allocationPct,
        };
      })
      .sort((a, b) => Number(b.currentValue || 0) - Number(a.currentValue || 0));
  }, [allocationRows]);

  const portfolioValueSeries = useMemo(() => {
    const lots = [];

    for (const entry of buysByDate) {
      const date = entry?.date;
      const picks = Array.isArray(entry?.picks) ? entry.picks : [];

      for (const pick of picks) {
        const ticker = String(pick?.ticker || "").trim().toUpperCase();
        const buyPrice = parseNumber(pick.buy_price);
        const shares = parseNumber(pick.shares);
        const dollarsAllocated =
          parseNumber(pick.dollars_allocated) ||
          (Number.isFinite(buyPrice) && Number.isFinite(shares)
            ? buyPrice * shares
            : null);

        if (!date || !ticker || !Number.isFinite(dollarsAllocated)) continue;

        lots.push({
          date,
          ticker,
          initialValue: dollarsAllocated,
          value: dollarsAllocated,
          active: false,
        });
      }
    }

    if (!lots.length) return [];

    const firstBuyDate = lots.reduce(
      (min, lot) => (String(lot.date) < String(min) ? lot.date : min),
      lots[0].date
    );

    const series = [];
    const sortedReturns = dailyReturns.filter(
      (row) => String(row.date || "") >= String(firstBuyDate)
    );

    for (const day of sortedReturns) {
      let totalValue = 0;
      let totalInvested = 0;

      for (const lot of lots) {
        if (String(lot.date) > String(day.date)) continue;

        totalInvested += lot.initialValue;

        if (!lot.active) {
          lot.active = true;
          lot.value = lot.initialValue;
        } else {
          const dailyReturn = day.returns?.[lot.ticker];

          if (Number.isFinite(dailyReturn)) {
            lot.value = lot.value * (1 + dailyReturn);
          }
        }

        totalValue += lot.value;
      }

      if (totalValue > 0) {
        series.push({
          date: day.date,
          totalValue,
          totalInvested,
          returnPct: totalInvested > 0 ? totalValue / totalInvested - 1 : null,
        });
      }
    }

    const currentTotalValue = uniqueHoldings.reduce(
      (sum, row) => sum + (Number(row.currentValue) || 0),
      0
    );

    const currentInvested = uniqueHoldings.reduce(
      (sum, row) => sum + (Number(row.totalInvested) || 0),
      0
    );

    if (currentTotalValue > 0) {
      const today = new Date().toISOString().slice(0, 10);
      const lastDate = series.length ? series[series.length - 1].date : null;

      if (lastDate !== today) {
        series.push({
          date: today,
          totalValue: currentTotalValue,
          totalInvested: currentInvested,
          returnPct:
            currentInvested > 0 ? currentTotalValue / currentInvested - 1 : null,
        });
      }
    }

    return series;
  }, [buysByDate, dailyReturns, uniqueHoldings]);

  const summary = useMemo(() => {
    const entries = buysByDate;
    const allPicks = entries.flatMap((entry) =>
      Array.isArray(entry.picks)
        ? entry.picks.map((pick) => ({ ...pick, date: entry.date }))
        : []
    );

    const uniqueTickers = new Set(
      allPicks
        .map((pick) => String(pick.ticker || "").trim().toUpperCase())
        .filter(Boolean)
    );

    const latestPicks = Array.isArray(latestEntry?.picks) ? latestEntry.picks : [];

    const avgLatestScore =
      latestPicks.length > 0
        ? latestPicks.reduce(
            (sum, pick) => sum + (Number(pick.combinedScore) || 0),
            0
          ) / latestPicks.length
        : null;

    return {
      daysLogged: entries.length,
      totalBuys: allPicks.length,
      uniqueTickers: uniqueTickers.size,
      latestDate: latestEntry?.date || null,
      latestCount: latestPicks.length,
      avgLatestScore,
    };
  }, [buysByDate, latestEntry]);

  if (loading) {
    return (
      <div style={pageShell}>
        <div style={messageBox}>Loading ranking model buys...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={pageShell}>
        <div style={errorBox}>{error}</div>
      </div>
    );
  }

  return (
    <div style={pageShell}>
      <section style={heroCard}>
        <div>
          <div style={eyebrow}>Majorah Paper Strategy</div>
          <h1 style={title}>Daily Top 10 Combined Score Buys</h1>
          <p style={subtitle}>
            This page shows the daily paper-buy log created by the ranking model:
            the top 10 stocks by combined score, their buy prices, model scores,
            and current allocation based on the latest screener prices.
          </p>
          <p style={metaText}>
            Strategy: {logData?.strategy?.name || "combined_score_top_10_daily_paper_buys"}
            {logData?.as_of ? ` · Updated ${formatDateTime(logData.as_of)}` : ""}
          </p>
        </div>

        <div style={metricGrid}>
          <MetricCard label="Days Logged" value={summary.daysLogged} />
          <MetricCard label="Total Paper Buys" value={summary.totalBuys} />
          <MetricCard label="Unique Tickers" value={summary.uniqueTickers} />
          <MetricCard label="Latest Date" value={formatDate(summary.latestDate)} />
          <MetricCard label="Latest Avg Score" value={formatScore(summary.avgLatestScore)} />
        </div>
      </section>

      {latestEntry ? (
        <section style={latestCard}>
          <div style={sectionHeader}>
            <div>
              <h2 style={sectionTitle}>Latest Top 10</h2>
              <p style={sectionSubtitle}>
                Paper buys logged for {formatDate(latestEntry.date)}.
              </p>
            </div>
          </div>

          <div style={latestGrid}>
            {(latestEntry.picks || []).map((pick) => {
              const ticker = String(pick.ticker || "").trim().toUpperCase();
              const buyPrice = parseNumber(pick.buy_price);
              const currentPrice = currentPriceByTicker[ticker];
              const returnPct =
                Number.isFinite(currentPrice) && Number.isFinite(buyPrice) && buyPrice > 0
                  ? currentPrice / buyPrice - 1
                  : null;

              return (
                <div key={`${latestEntry.date}-${ticker}`} style={latestPickCard}>
                  <div style={latestTopRow}>
                    <div style={rankBubble}>#{pick.rank}</div>
                    <Link
                      to={`${STOCK_DETAIL_PATH}/${encodeURIComponent(ticker)}`}
                      style={tickerLinkLarge}
                    >
                      {ticker}
                    </Link>
                    <span style={{ ...returnPill, ...returnColor(returnPct) }}>
                      {formatPercent(returnPct)}
                    </span>
                  </div>

                  <div style={companyName}>{pick.name || "—"}</div>

                  <div style={miniStatsGrid}>
                    <MiniStat label="Buy Price" value={formatMoney(buyPrice)} />
                    <MiniStat label="Current" value={formatMoney(currentPrice)} />
                    <MiniStat label="Shares" value={formatShares(pick.shares)} />
                    <MiniStat label="Combined" value={formatScore(pick.combinedScore)} />
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ) : null}

      <section style={controlCard}>
        <div>
          <h2 style={sectionTitle}>Buy History</h2>
          <p style={sectionSubtitle}>
            Filter by date or ticker/company. Each date contains the model’s top 10 picks.
          </p>
        </div>

        <div style={controls}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search ticker or company..."
            style={searchInput}
          />

          <select
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            style={selectInput}
          >
            <option value="ALL">All dates</option>
            {dateOptions.map((date) => (
              <option key={date} value={date}>
                {formatDate(date)}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={() => setShowOnlyLatest((prev) => !prev)}
            style={showLatestButton(showOnlyLatest)}
          >
            {showOnlyLatest ? "Showing Latest" : "Show Latest Only"}
          </button>
        </div>
      </section>

      <section style={historyCard}>
        {filteredEntries.length === 0 ? (
          <div style={messageBox}>No matching paper buys found.</div>
        ) : (
          filteredEntries.map((entry) => (
            <DailyBuyTable
              key={entry.date}
              entry={entry}
              currentPriceByTicker={currentPriceByTicker}
            />
          ))
        )}
      </section>

      <section style={allocationCard}>
        <div style={sectionHeader}>
          <div>
            <h2 style={sectionTitle}>Current Allocation Ledger</h2>
            <p style={sectionSubtitle}>
              Every logged BUY flattened into one table. Allocation % is based on current value of each buy lot.
            </p>
          </div>
        </div>

        <div style={allocationMetrics}>
          <MetricCardDark label="Total Lots" value={allocationSummary.totalLots} />
          <MetricCardDark label="Total Invested" value={formatDollars(allocationSummary.totalInvested)} />
          <MetricCardDark label="Current Value" value={formatDollars(allocationSummary.totalCurrentValue)} />
          <MetricCardDark label="Total Return" value={formatPercent(allocationSummary.totalReturnPct)} />
        </div>

        <div style={tableWrap}>
          <table style={allocationTable}>
            <thead>
              <tr>
                <th style={thLeft}>Ticker</th>
                <th style={thLeft}>Date</th>
                <th style={thLeft}>Action</th>
                <th style={thRight}>Shares Bought</th>
                <th style={thRight}>Total Amount</th>
                <th style={thRight}>Buy Price</th>
                <th style={thRight}>Current Price</th>
                <th style={thRight}>Current Value</th>
                <th style={thRight}>Return</th>
                <th style={thRight}>Current % Allocation</th>
              </tr>
            </thead>

            <tbody>
              {allocationRows.length === 0 ? (
                <tr>
                  <td style={td} colSpan={10}>
                    No allocation rows found.
                  </td>
                </tr>
              ) : (
                allocationRows.map((row, index) => (
                  <tr
                    key={`${row.date}-${row.ticker}-${index}`}
                    style={index % 2 === 0 ? rowEven : rowOdd}
                  >
                    <td style={td}>
                      <Link
                        to={`${STOCK_DETAIL_PATH}/${encodeURIComponent(row.ticker)}`}
                        style={tickerLink}
                      >
                        {row.ticker}
                      </Link>
                    </td>
                    <td style={td}>{formatDate(row.date)}</td>
                    <td style={td}>
                      <span style={buyPill}>{row.action || "BUY"}</span>
                    </td>
                    <td style={tdRight}>{formatShares(row.shares)}</td>
                    <td style={tdRight}>{formatDollars(row.dollarsAllocated)}</td>
                    <td style={tdRight}>{formatMoney(row.buyPrice)}</td>
                    <td style={tdRight}>{formatMoney(row.currentPrice)}</td>
                    <td style={tdRight}>{formatDollars(row.currentValue)}</td>
                    <td style={tdRight}>
                      <span style={{ ...returnPillInline, ...returnColor(row.returnPct) }}>
                        {formatPercent(row.returnPct)}
                      </span>
                    </td>
                    <td style={tdRight}>
                      <span style={allocationPill}>
                        {formatAllocationPercent(row.allocationPct)}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>

            <tfoot>
              <tr>
                <td style={tfootCell} colSpan={4}>
                  Total
                </td>
                <td style={tfootRight}>{formatDollars(allocationSummary.totalInvested)}</td>
                <td style={tfootRight}>—</td>
                <td style={tfootRight}>—</td>
                <td style={tfootRight}>{formatDollars(allocationSummary.totalCurrentValue)}</td>
                <td style={tfootRight}>{formatPercent(allocationSummary.totalReturnPct)}</td>
                <td style={tfootRight}>100.00%</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      <section style={uniqueHoldingsCard}>
        <div style={sectionHeader}>
          <div>
            <h2 style={sectionTitle}>Current Unique Holdings</h2>
            <p style={sectionSubtitle}>
              Aggregated by ticker across all model BUY lots. Buy price is weighted average buy price.
            </p>
          </div>
        </div>

        <div style={tableWrap}>
          <table style={uniqueHoldingsTable}>
            <thead>
              <tr>
                <th style={thLeft}>Ticker</th>
                <th style={thLeft}>Company</th>
                <th style={thRight}>Amount Invested</th>
                <th style={thRight}>Avg Buy Price</th>
                <th style={thRight}>Shares Held</th>
                <th style={thRight}>Current Price</th>
                <th style={thRight}>Current Value</th>
                <th style={thRight}>Return</th>
                <th style={thRight}>Current % Allocation</th>
                <th style={thRight}>Lots</th>
                <th style={thRight}>First Buy</th>
                <th style={thRight}>Last Buy</th>
              </tr>
            </thead>

            <tbody>
              {uniqueHoldings.length === 0 ? (
                <tr>
                  <td style={td} colSpan={12}>
                    No current holdings found.
                  </td>
                </tr>
              ) : (
                uniqueHoldings.map((row, index) => (
                  <tr
                    key={row.ticker}
                    style={index % 2 === 0 ? rowEven : rowOdd}
                  >
                    <td style={td}>
                      <Link
                        to={`${STOCK_DETAIL_PATH}/${encodeURIComponent(row.ticker)}`}
                        style={tickerLink}
                      >
                        {row.ticker}
                      </Link>
                    </td>
                    <td style={tdName}>{row.name || "—"}</td>
                    <td style={tdRight}>{formatDollars(row.totalInvested)}</td>
                    <td style={tdRight}>{formatMoney(row.avgBuyPrice)}</td>
                    <td style={tdRight}>{formatShares(row.totalShares)}</td>
                    <td style={tdRight}>{formatMoney(row.currentPrice)}</td>
                    <td style={tdRight}>{formatDollars(row.currentValue)}</td>
                    <td style={tdRight}>
                      <span style={{ ...returnPillInline, ...returnColor(row.returnPct) }}>
                        {formatPercent(row.returnPct)}
                      </span>
                    </td>
                    <td style={tdRight}>
                      <span style={allocationPill}>
                        {formatAllocationPercent(row.allocationPct)}
                      </span>
                    </td>
                    <td style={tdRight}>{row.lots}</td>
                    <td style={tdRight}>{formatDate(row.firstBuyDate)}</td>
                    <td style={tdRight}>{formatDate(row.lastBuyDate)}</td>
                  </tr>
                ))
              )}
            </tbody>

            <tfoot>
              <tr>
                <td style={tfootCell} colSpan={2}>
                  Total
                </td>
                <td style={tfootRight}>
                  {formatDollars(
                    uniqueHoldings.reduce(
                      (sum, row) => sum + (Number(row.totalInvested) || 0),
                      0
                    )
                  )}
                </td>
                <td style={tfootRight}>—</td>
                <td style={tfootRight}>
                  {formatShares(
                    uniqueHoldings.reduce(
                      (sum, row) => sum + (Number(row.totalShares) || 0),
                      0
                    )
                  )}
                </td>
                <td style={tfootRight}>—</td>
                <td style={tfootRight}>
                  {formatDollars(
                    uniqueHoldings.reduce(
                      (sum, row) => sum + (Number(row.currentValue) || 0),
                      0
                    )
                  )}
                </td>
                <td style={tfootRight}>—</td>
                <td style={tfootRight}>100.00%</td>
                <td style={tfootRight}>
                  {uniqueHoldings.reduce((sum, row) => sum + (Number(row.lots) || 0), 0)}
                </td>
                <td style={tfootRight}>—</td>
                <td style={tfootRight}>—</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      <section style={portfolioChartCard}>
        <div style={sectionHeader}>
          <div>
            <h2 style={sectionTitle}>Model Portfolio Value Over Time</h2>
            <p style={sectionSubtitle}>
              Rebuilt from logged BUY lots and daily return data. Final point uses current screener prices when available.
            </p>
          </div>
        </div>

        <PortfolioValueLineChart data={portfolioValueSeries} />
      </section>
    </div>
  );
}

function DailyBuyTable({ entry, currentPriceByTicker }) {
  const [expanded, setExpanded] = useState(true);

  const picks = Array.isArray(entry.picks) ? entry.picks : [];

  const avgScore =
    picks.length > 0
      ? picks.reduce((sum, pick) => sum + (Number(pick.combinedScore) || 0), 0) /
        picks.length
      : null;

  return (
    <div style={dayBlock}>
      <button type="button" onClick={() => setExpanded((prev) => !prev)} style={dayHeader}>
        <div>
          <div style={dayTitle}>{formatDate(entry.date)}</div>
          <div style={daySubtitle}>
            {picks.length} paper buys
            {entry.data_as_of ? ` · Screener as of ${entry.data_as_of}` : ""}
            {entry.smart_money_as_of ? ` · Smart money as of ${entry.smart_money_as_of}` : ""}
          </div>
        </div>

        <div style={dayRight}>
          <span style={{ ...scorePill, ...scoreColor(avgScore) }}>
            Avg Score {formatScore(avgScore)}
          </span>
          <span style={chevron}>{expanded ? "▾" : "▸"}</span>
        </div>
      </button>

      {expanded ? (
        <div style={tableWrap}>
          <table style={table}>
            <thead>
              <tr>
                <th style={thRight}>Rank</th>
                <th style={thLeft}>Ticker</th>
                <th style={thLeft}>Company</th>
                <th style={thRight}>Buy Price</th>
                <th style={thRight}>Current</th>
                <th style={thRight}>Return</th>
                <th style={thRight}>Dollars</th>
                <th style={thRight}>Shares</th>
                <th style={thRight}>Combined</th>
                <th style={thRight}>Quality</th>
                <th style={thRight}>Value</th>
                <th style={thRight}>Sale</th>
                <th style={thRight}>Defensive</th>
                <th style={thRight}>Smart Money</th>
              </tr>
            </thead>

            <tbody>
              {picks.map((pick, index) => {
                const ticker = String(pick.ticker || "").trim().toUpperCase();
                const buyPrice = parseNumber(pick.buy_price);
                const currentPrice = currentPriceByTicker[ticker];

                const returnPct =
                  Number.isFinite(currentPrice) &&
                  Number.isFinite(buyPrice) &&
                  buyPrice > 0
                    ? currentPrice / buyPrice - 1
                    : null;

                return (
                  <tr
                    key={`${entry.date}-${ticker}-${index}`}
                    style={index % 2 === 0 ? rowEven : rowOdd}
                  >
                    <td style={tdRight}>{pick.rank || index + 1}</td>
                    <td style={td}>
                      <Link
                        to={`${STOCK_DETAIL_PATH}/${encodeURIComponent(ticker)}`}
                        style={tickerLink}
                      >
                        {ticker}
                      </Link>
                    </td>
                    <td style={tdName}>{pick.name || "—"}</td>
                    <td style={tdRight}>{formatMoney(buyPrice)}</td>
                    <td style={tdRight}>{formatMoney(currentPrice)}</td>
                    <td style={tdRight}>
                      <span style={{ ...returnPillInline, ...returnColor(returnPct) }}>
                        {formatPercent(returnPct)}
                      </span>
                    </td>
                    <td style={tdRight}>{formatDollars(pick.dollars_allocated)}</td>
                    <td style={tdRight}>{formatShares(pick.shares)}</td>
                    <td style={tdRight}>
                      <span style={{ ...scorePill, ...scoreColor(pick.combinedScore) }}>
                        {formatScore(pick.combinedScore)}
                      </span>
                    </td>
                    <td style={tdRight}>{formatScore(pick.qualityScore)}</td>
                    <td style={tdRight}>{formatScore(pick.valuationScore)}</td>
                    <td style={tdRight}>{formatScore(pick.scaleScore)}</td>
                    <td style={tdRight}>{formatScore(pick.defensiveScore)}</td>
                    <td style={tdRight}>{formatScore(pick.smartMoneyScore)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

function MetricCard({ label, value }) {
  return (
    <div style={metricCard}>
      <div style={metricLabel}>{label}</div>
      <div style={metricValue}>{value ?? "—"}</div>
    </div>
  );
}

function MetricCardDark({ label, value }) {
  return (
    <div style={metricCardDark}>
      <div style={metricLabelDark}>{label}</div>
      <div style={metricValueDark}>{value ?? "—"}</div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div style={miniStat}>
      <div style={miniLabel}>{label}</div>
      <div style={miniValue}>{value}</div>
    </div>
  );
}

function PortfolioValueLineChart({ data }) {
  const width = 980;
  const height = 320;
  const padLeft = 72;
  const padRight = 26;
  const padTop = 28;
  const padBottom = 48;

  const clean = Array.isArray(data)
    ? data.filter((d) => Number.isFinite(Number(d.totalValue)))
    : [];

  if (clean.length < 2) {
    return (
      <div style={chartEmpty}>
        Not enough portfolio history yet to draw a line chart.
      </div>
    );
  }

  const values = clean.map((d) => Number(d.totalValue));
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);

  const paddedMin = minValue * 0.98;
  const paddedMax = maxValue * 1.02;
  const valueRange = paddedMax - paddedMin || 1;

  const xFor = (index) => {
    if (clean.length === 1) return padLeft;
    return padLeft + (index / (clean.length - 1)) * (width - padLeft - padRight);
  };

  const yFor = (value) => {
    return padTop + ((paddedMax - value) / valueRange) * (height - padTop - padBottom);
  };

  const points = clean
    .map((d, index) => `${xFor(index)},${yFor(Number(d.totalValue))}`)
    .join(" ");

  const investedPoints = clean
    .filter((d) => Number.isFinite(Number(d.totalInvested)))
    .map((d, index) => `${xFor(index)},${yFor(Number(d.totalInvested))}`)
    .join(" ");

  const first = clean[0];
  const last = clean[clean.length - 1];

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((p) => {
    const value = paddedMax - p * valueRange;
    return {
      value,
      y: yFor(value),
    };
  });

  return (
    <div style={chartShell}>
      <div style={chartTopStats}>
        <div>
          <div style={chartStatLabel}>Latest Value</div>
          <div style={chartStatValue}>{formatDollars(last.totalValue)}</div>
        </div>

        <div>
          <div style={chartStatLabel}>Total Invested</div>
          <div style={chartStatValue}>{formatDollars(last.totalInvested)}</div>
        </div>

        <div>
          <div style={chartStatLabel}>Total Return</div>
          <div style={chartStatValue}>{formatPercent(last.returnPct)}</div>
        </div>

        <div>
          <div style={chartStatLabel}>History</div>
          <div style={chartStatValue}>
            {formatDate(first.date)} → {formatDate(last.date)}
          </div>
        </div>
      </div>

      <div style={chartScroll}>
        <svg
          viewBox={`0 0 ${width} ${height}`}
          width="100%"
          height="320"
          role="img"
          aria-label="Model portfolio value over time"
        >
          <rect x="0" y="0" width={width} height={height} rx="18" fill="#ffffff" />

          {yTicks.map((tick, index) => (
            <g key={index}>
              <line
                x1={padLeft}
                x2={width - padRight}
                y1={tick.y}
                y2={tick.y}
                stroke="#e2e8f0"
                strokeWidth="1"
              />
              <text
                x={padLeft - 10}
                y={tick.y + 4}
                textAnchor="end"
                fontSize="11"
                fill="#64748b"
              >
                {formatCompactDollars(tick.value)}
              </text>
            </g>
          ))}

          <line
            x1={padLeft}
            x2={width - padRight}
            y1={height - padBottom}
            y2={height - padBottom}
            stroke="#cbd5e1"
            strokeWidth="1"
          />

          <line
            x1={padLeft}
            x2={padLeft}
            y1={padTop}
            y2={height - padBottom}
            stroke="#cbd5e1"
            strokeWidth="1"
          />

          {investedPoints ? (
            <polyline
              points={investedPoints}
              fill="none"
              stroke="#94a3b8"
              strokeWidth="2"
              strokeDasharray="6 6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ) : null}

          <polyline
            points={points}
            fill="none"
            stroke="#2563eb"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {clean.length <= 45
            ? clean.map((d, index) => (
                <circle
                  key={`${d.date}-${index}`}
                  cx={xFor(index)}
                  cy={yFor(Number(d.totalValue))}
                  r="3.5"
                  fill="#2563eb"
                  stroke="#ffffff"
                  strokeWidth="1.5"
                />
              ))
            : null}

          <text
            x={padLeft}
            y={height - 18}
            fontSize="11"
            fill="#64748b"
            textAnchor="start"
          >
            {formatDate(first.date)}
          </text>

          <text
            x={width - padRight}
            y={height - 18}
            fontSize="11"
            fill="#64748b"
            textAnchor="end"
          >
            {formatDate(last.date)}
          </text>

          <text
            x={width - padRight}
            y={padTop - 8}
            fontSize="11"
            fill="#2563eb"
            textAnchor="end"
            fontWeight="800"
          >
            Portfolio Value
          </text>

          <text
            x={width - padRight}
            y={padTop + 10}
            fontSize="11"
            fill="#64748b"
            textAnchor="end"
            fontWeight="800"
          >
            Dashed = Invested Capital
          </text>
        </svg>
      </div>
    </div>
  );
}

const pageShell = {
  minHeight: "100vh",
  background: "linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)",
  padding: "22px 10px 34px",
  fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
  color: "#0f172a",
};

const heroCard = {
  maxWidth: 1680,
  margin: "0 auto 18px",
  padding: 22,
  background:
    "radial-gradient(circle at 16% 12%, rgba(56, 189, 248, 0.23), transparent 32%), linear-gradient(135deg, #0f172a 0%, #1e293b 55%, #334155 100%)",
  color: "white",
  borderRadius: 24,
  boxShadow: "0 22px 52px rgba(15, 23, 42, 0.2)",
};

const eyebrow = {
  marginBottom: 8,
  color: "#93c5fd",
  fontSize: 11,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  fontWeight: 900,
};

const title = {
  margin: 0,
  fontSize: 32,
  letterSpacing: "-0.05em",
  lineHeight: 1.02,
};

const subtitle = {
  maxWidth: 980,
  marginTop: 10,
  color: "#cbd5e1",
  lineHeight: 1.65,
  fontSize: 14,
};

const metaText = {
  marginTop: 8,
  color: "#93c5fd",
  fontSize: 12,
  fontWeight: 750,
};

const metricGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
  gap: 12,
  marginTop: 22,
};

const metricCard = {
  padding: 15,
  borderRadius: 18,
  background: "rgba(255, 255, 255, 0.1)",
  border: "1px solid rgba(255, 255, 255, 0.16)",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
};

const metricLabel = {
  fontSize: 10,
  color: "#cbd5e1",
  fontWeight: 850,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
};

const metricValue = {
  marginTop: 8,
  fontSize: 22,
  fontWeight: 950,
  letterSpacing: "-0.04em",
};

const metricCardDark = {
  padding: 15,
  borderRadius: 18,
  background: "#0f172a",
  border: "1px solid #1e293b",
  boxShadow: "0 12px 26px rgba(15, 23, 42, 0.12)",
};

const metricLabelDark = {
  fontSize: 10,
  color: "#cbd5e1",
  fontWeight: 850,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
};

const metricValueDark = {
  marginTop: 8,
  fontSize: 22,
  color: "white",
  fontWeight: 950,
  letterSpacing: "-0.04em",
};

const latestCard = {
  maxWidth: 1680,
  margin: "0 auto 18px",
  padding: 18,
  border: "1px solid #e2e8f0",
  borderRadius: 22,
  background: "white",
  boxShadow: "0 16px 36px rgba(15, 23, 42, 0.1)",
};

const sectionHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  marginBottom: 14,
};

const sectionTitle = {
  margin: 0,
  fontSize: 17,
  letterSpacing: "-0.03em",
};

const sectionSubtitle = {
  margin: "4px 0 0",
  color: "#64748b",
  fontSize: 12,
};

const latestGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
  gap: 12,
};

const latestPickCard = {
  padding: 14,
  borderRadius: 18,
  border: "1px solid #e2e8f0",
  background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
};

const latestTopRow = {
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const rankBubble = {
  width: 34,
  height: 34,
  borderRadius: 999,
  background: "#eff6ff",
  color: "#1d4ed8",
  border: "1px solid #bfdbfe",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 12,
  fontWeight: 950,
};

const tickerLinkLarge = {
  color: "#0f172a",
  textDecoration: "none",
  fontWeight: 950,
  fontSize: 18,
  letterSpacing: "-0.03em",
};

const companyName = {
  marginTop: 8,
  color: "#64748b",
  fontSize: 12,
  lineHeight: 1.35,
  minHeight: 32,
};

const miniStatsGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 8,
  marginTop: 12,
};

const miniStat = {
  padding: 9,
  borderRadius: 13,
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
};

const miniLabel = {
  fontSize: 9,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "#64748b",
  fontWeight: 850,
};

const miniValue = {
  marginTop: 4,
  fontSize: 13,
  color: "#0f172a",
  fontWeight: 900,
};

const controlCard = {
  maxWidth: 1680,
  margin: "0 auto 18px",
  padding: 18,
  border: "1px solid #e2e8f0",
  borderRadius: 22,
  background: "rgba(255, 255, 255, 0.9)",
  boxShadow: "0 14px 32px rgba(15, 23, 42, 0.08)",
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  alignItems: "center",
  flexWrap: "wrap",
};

const controls = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "flex-end",
  flex: "1 1 560px",
};

const searchInput = {
  minWidth: 260,
  flex: "1 1 260px",
  maxWidth: 420,
  padding: "10px 12px",
  border: "1px solid #cbd5e1",
  borderRadius: 13,
  fontSize: 12,
  background: "white",
  outline: "none",
};

const selectInput = {
  minWidth: 170,
  padding: "10px 12px",
  border: "1px solid #cbd5e1",
  borderRadius: 13,
  fontSize: 12,
  background: "white",
  outline: "none",
};

function showLatestButton(active) {
  return {
    padding: "10px 13px",
    borderRadius: 13,
    border: active ? "1px solid #2563eb" : "1px solid #cbd5e1",
    background: active ? "#eff6ff" : "white",
    color: active ? "#1d4ed8" : "#334155",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 850,
  };
}

const historyCard = {
  maxWidth: 1680,
  margin: "0 auto",
};

const allocationCard = {
  maxWidth: 1680,
  margin: "18px auto 0",
  padding: 18,
  border: "1px solid #e2e8f0",
  borderRadius: 22,
  background: "white",
  boxShadow: "0 16px 36px rgba(15, 23, 42, 0.1)",
};

const allocationMetrics = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 12,
  marginBottom: 16,
};

const dayBlock = {
  marginBottom: 16,
  border: "1px solid #e2e8f0",
  borderRadius: 22,
  overflow: "hidden",
  background: "white",
  boxShadow: "0 16px 36px rgba(15, 23, 42, 0.08)",
};

const dayHeader = {
  width: "100%",
  padding: 16,
  border: "none",
  borderBottom: "1px solid #e2e8f0",
  background: "#ffffff",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  textAlign: "left",
};

const dayTitle = {
  fontSize: 17,
  fontWeight: 950,
  color: "#0f172a",
  letterSpacing: "-0.03em",
};

const daySubtitle = {
  marginTop: 4,
  color: "#64748b",
  fontSize: 12,
};

const dayRight = {
  display: "flex",
  alignItems: "center",
  gap: 10,
};

const chevron = {
  fontSize: 18,
  color: "#64748b",
};

const tableWrap = {
  overflowX: "auto",
};

const table = {
  width: "100%",
  minWidth: 1380,
  borderCollapse: "separate",
  borderSpacing: 0,
};

const allocationTable = {
  width: "100%",
  minWidth: 1180,
  borderCollapse: "separate",
  borderSpacing: 0,
  border: "1px solid #e2e8f0",
  borderRadius: 16,
  overflow: "hidden",
};

const thLeft = {
  textAlign: "left",
  padding: "9px 10px",
  fontSize: 10,
  color: "#475569",
  borderBottom: "1px solid #e2e8f0",
  background: "#f8fafc",
  whiteSpace: "nowrap",
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: "0.035em",
};

const thRight = {
  ...thLeft,
  textAlign: "right",
};

const td = {
  padding: "9px 10px",
  fontSize: 12,
  color: "#1e293b",
  borderBottom: "1px solid #eef2f7",
  whiteSpace: "nowrap",
};

const tdName = {
  ...td,
  maxWidth: 260,
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const tdRight = {
  ...td,
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
};

const tfootCell = {
  padding: "11px 10px",
  fontSize: 12,
  fontWeight: 950,
  color: "#0f172a",
  borderTop: "1px solid #cbd5e1",
  background: "#f8fafc",
};

const tfootRight = {
  ...tfootCell,
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
};

const rowEven = {
  background: "#ffffff",
};

const rowOdd = {
  background: "#fbfdff",
};

const tickerLink = {
  color: "#2563eb",
  textDecoration: "none",
  fontWeight: 900,
};

const scorePill = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 54,
  padding: "5px 8px",
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 950,
  border: "1px solid transparent",
};

const excellentScorePill = {
  color: "#047857",
  background: "#ecfdf5",
  borderColor: "#a7f3d0",
};

const goodScorePill = {
  color: "#15803d",
  background: "#f0fdf4",
  borderColor: "#bbf7d0",
};

const okayScorePill = {
  color: "#a16207",
  background: "#fefce8",
  borderColor: "#fde68a",
};

const weakScorePill = {
  color: "#b91c1c",
  background: "#fff1f2",
  borderColor: "#fecdd3",
};

const neutralScorePill = {
  color: "#64748b",
  background: "#f8fafc",
  borderColor: "#e2e8f0",
};

const returnPill = {
  marginLeft: "auto",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 72,
  padding: "5px 8px",
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 950,
  border: "1px solid transparent",
};

const returnPillInline = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 72,
  padding: "5px 8px",
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 950,
  border: "1px solid transparent",
};

const positivePill = {
  color: "#047857",
  background: "#ecfdf5",
  borderColor: "#a7f3d0",
};

const negativePill = {
  color: "#b91c1c",
  background: "#fff1f2",
  borderColor: "#fecdd3",
};

const neutralPill = {
  color: "#64748b",
  background: "#f8fafc",
  borderColor: "#e2e8f0",
};

const buyPill = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 46,
  padding: "5px 8px",
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 950,
  color: "#1d4ed8",
  background: "#eff6ff",
  border: "1px solid #bfdbfe",
};

const allocationPill = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 72,
  padding: "5px 8px",
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 950,
  color: "#4338ca",
  background: "#eef2ff",
  border: "1px solid #c7d2fe",
};

const uniqueHoldingsCard = {
  maxWidth: 1680,
  margin: "18px auto 0",
  padding: 18,
  border: "1px solid #e2e8f0",
  borderRadius: 22,
  background: "white",
  boxShadow: "0 16px 36px rgba(15, 23, 42, 0.1)",
};

const uniqueHoldingsTable = {
  width: "100%",
  minWidth: 1380,
  borderCollapse: "separate",
  borderSpacing: 0,
  border: "1px solid #e2e8f0",
  borderRadius: 16,
  overflow: "hidden",
};

const portfolioChartCard = {
  maxWidth: 1680,
  margin: "18px auto 0",
  padding: 18,
  border: "1px solid #e2e8f0",
  borderRadius: 22,
  background: "white",
  boxShadow: "0 16px 36px rgba(15, 23, 42, 0.1)",
};

const chartShell = {
  border: "1px solid #e2e8f0",
  borderRadius: 18,
  background: "#ffffff",
  overflow: "hidden",
};

const chartTopStats = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 12,
  padding: 14,
  borderBottom: "1px solid #e2e8f0",
  background: "#f8fafc",
};

const chartStatLabel = {
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  fontWeight: 900,
  color: "#64748b",
};

const chartStatValue = {
  marginTop: 5,
  fontSize: 18,
  fontWeight: 950,
  color: "#0f172a",
  letterSpacing: "-0.04em",
};

const chartScroll = {
  width: "100%",
  overflowX: "auto",
  padding: 10,
};

const chartEmpty = {
  padding: 18,
  color: "#64748b",
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: 14,
};

const messageBox = {
  maxWidth: 1680,
  margin: "0 auto",
  padding: 20,
  color: "#475569",
  background: "white",
  borderRadius: 16,
  border: "1px solid #e2e8f0",
};

const errorBox = {
  maxWidth: 1680,
  margin: "0 auto",
  padding: 14,
  background: "#fff1f2",
  color: "#9f1239",
  border: "1px solid #fecdd3",
  borderRadius: 12,
};